import React, { useEffect, useState, useRef } from 'react';
import { Bell, LogOut, User, Menu, Sun, Moon, Wallet } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  clearError,
} from '../../store/slices/notificationSlice';
import { fetchMyProfile } from '../../store/slices/profileSlice';
import useNotificationSocket from '../../hooks/useNotificationSocket';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { format, isValid } from 'date-fns';

const Navbar = ({ isCollapsed, onMenuToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  // Redux state
  const { user: authUser } = useSelector((state) => state.auth);
  const { profile, isLoading: profileLoading, error: profileError } = useSelector((s) => s.profile);
  const { list: notifications, unreadCount, isLoading: notifLoading, error: notifError } = useSelector(s => s.notification);

  // Local state
  const [page, setPage] = useState(1);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverTriggerRef = useRef(null);

  // WebSocket
  useNotificationSocket();

  // Load notifications
  useEffect(() => {
    dispatch(fetchNotifications({ page, pageSize: 10 }));
    dispatch(fetchUnreadCount());
  }, [dispatch, page]);

  // Load profile
  useEffect(() => {
    if (authUser?.id) {
      dispatch(fetchMyProfile());
    }
  }, [dispatch, authUser?.id]);

  // Toast errors
  useEffect(() => {
    if (notifError) {
      toast.error(notifError);
      dispatch(clearError());
    }
  }, [notifError, dispatch]);

  useEffect(() => {
    if (profileError) {
      console.warn('Profile load failed:', profileError);
      // Optional: toast.error('Failed to load profile');
    }
  }, [profileError]);

  const handleLogout = () => {
    setTheme('light');
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const getInitials = (first = 'U', last = 'S') =>
    `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

  const handleMarkRead = async (id) => {
    await dispatch(markNotificationRead(id));
  };

  const handleMarkAllRead = async () => {
    await dispatch(markAllNotificationsRead());
    toast.success('All notifications marked as read');
  };

  const handleViewAll = () => {
    setPopoverOpen(false);
    navigate('/notifications');
  };

  // Use profile from API
  const displayName = profile?.full_name || 'User';
  const displayRole = profile?.role_name || 'Employee';
  const profileImage = profile?.profile_img;
  const firstName = profile?.first_name || 'U';
  const lastName = profile?.last_name || 'S';

  return (
    <header
      className={cn(
        'w-full fixed top-0 right-0 z-30 h-16 border-b border-border bg-card/95 backdrop-blur-sm transition-all duration-300 flex items-center justify-between'
      )}
    >
      <div className="flex items-center justify-between md:justify-end w-full h-full px-4 md:px-6">
        {/* Mobile Hamburger */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onMenuToggle}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Right Side Icons */}
        <div className="flex items-center gap-2">

          {/* Theme Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                >
                  <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Toggle {theme === 'dark' ? 'Light' : 'Dark'} Mode</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Notifications */}
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild ref={popoverTriggerRef}>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-6 flex items-center justify-center p-0 text-[10px] bg-destructive">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 mr-4" align="end">
              <div className="border-b p-4 flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">Notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    {unreadCount} unread
                  </p>
                </div>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                    Mark all read
                  </Button>
                )}
              </div>

              <ScrollArea className="max-h-80 overflow-auto">
                {notifLoading && !notifications.length ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
                ) : notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n?.id}
                      className={cn(
                        'p-3 border-b hover:bg-muted/50 cursor-pointer transition-colors',
                        !n?.read && 'bg-accent/5'
                      )}
                      onClick={() => !n?.read && handleMarkRead(n?.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{n?.message}</p>
                          <p className="text-xs text-muted-foreground">
                            {n?.created_at ? (
                              (date => isValid(date) ? format(date, 'MMM d, yyyy h:mm a') : 'Invalid Date')(
                                n.created_at?.toDate?.() || new Date(n.created_at)
                              )
                            ) : (
                              '—'
                            )}
                          </p>
                          {n?.type_display && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {n?.type_display}
                            </Badge>
                          )}
                        </div>
                        {!n?.read && <div className="w-2 h-2 rounded-full bg-primary mt-1" />}
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>

              <div className="p-2 border-t">
                <Button
                  variant="ghost"
                  className="w-full text-xs"
                  size="sm"
                  onClick={handleViewAll}
                >
                  View all
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* User Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 gap-2 px-2">
                <Avatar className="h-8 w-8">
                  {profileLoading ? (
                    <AvatarFallback className="animate-pulse bg-muted" />
                  ) : profileImage ? (
                    <AvatarImage src={profileImage} alt={displayName} />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xs font-semibold">
                      {getInitials(firstName, lastName)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="hidden md:flex flex-col items-start text-left">
                  <span className="text-sm font-medium leading-none">
                    {displayName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {displayRole}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{profile?.email}</p>
                </div>
              </DropdownMenuLabel>


              {/* <DropdownMenuLabel>
                <div className="flex items-center justify-between pt-2 border-t border-border">


                  <span className="flex items-center text-sm font-bold text-green-600 gap-4">
                    <Wallet className="h-3.5 w-3.5 text-muted-foreground" /> ₹{Number(profile?.wallet_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </DropdownMenuLabel> */}


              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer flex items-center">
                  <User className="mr-2 h-4 w-4" /> Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Navbar;